import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken, saveUser } from "@/lib/api";
import { User, BookOpen, GraduationCap, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  full_name: "", academic_year: "", department: "",
  register_number: "", college_name: "", mobile: "",
  email: "", password: "", confirm_password: "",
};

export default function RegisterPage() {
  const [role, setRole] = useState(null); // null | 'student' | 'staff'
  const [form, setForm] = useState(emptyForm);
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return toast.error("Passwords don't match");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!terms) return toast.error("Please accept the Terms and Conditions");
    setLoading(true);
    try {
      const payload = {
        email: form.email, password: form.password, role,
        full_name: form.full_name, academic_year: form.academic_year,
        department: form.department, register_number: form.register_number,
        college_name: form.college_name, mobile: form.mobile,
        terms_accepted: true,
      };
      const res = await api.post("/auth/register", payload);
      const data = res.data;
      if (data.status === "pending") {
        setPendingMsg(data.message || "Your registration is pending administrator approval.");
        toast.info("Awaiting admin approval");
        return;
      }
      // student: auto-login
      setToken(data.token); saveUser(data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      nav("/student");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  if (pendingMsg) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="pending-approval-panel">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
          <h2 className="mt-3 font-display font-black text-2xl text-slate-900">Awaiting Approval</h2>
          <p className="mt-2 text-sm text-slate-500">{pendingMsg}</p>
          <Link to="/login" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Back to sign-in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0B1120] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-black text-lg leading-none">TrillionBytex<span className="text-blue-400">edu</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
            </div>
          </Link>
          <Link to="/login" data-testid="reg-signin-link" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Sign in</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {!role && (
          <>
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-2">Register</div>
              <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">Choose your account type</h1>
              <p className="mt-2 text-sm text-slate-500">Students get instant access. Staff accounts require admin approval before sign-in.</p>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <button data-testid="pick-student" onClick={() => setRole("student")}
                className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-500 card-hover group">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><User className="w-6 h-6" /></div>
                <h3 className="mt-4 font-display font-black text-xl text-slate-900">Student Registration</h3>
                <p className="mt-1 text-sm text-slate-500">Access courses, brain games, coding challenges, and your AI mentor 02.</p>
                <div className="mt-4 text-sm text-blue-600 font-semibold group-hover:underline">Continue as student →</div>
              </button>
              <button data-testid="pick-staff" onClick={() => setRole("staff")}
                className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-500 card-hover group">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><BookOpen className="w-6 h-6" /></div>
                <h3 className="mt-4 font-display font-black text-xl text-slate-900">Staff Registration</h3>
                <p className="mt-1 text-sm text-slate-500">Create courses, host live classes, track students. Requires admin approval.</p>
                <div className="mt-4 text-sm text-emerald-600 font-semibold group-hover:underline">Continue as staff →</div>
              </button>
            </div>
          </>
        )}

        {role && (
          <div className="max-w-2xl">
            <button onClick={() => { setRole(null); setForm(emptyForm); setTerms(false); }} data-testid="back-to-role"
              className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Choose different role</button>
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-1">Register as</div>
            <h1 className="font-display font-black text-3xl text-slate-900 capitalize">{role} account</h1>
            <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="register-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <input required data-testid="reg-full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Academic Year" required>
                  <input required data-testid="reg-academic_year" value={form.academic_year} onChange={(e) => setForm({ ...form, academic_year: e.target.value })} placeholder={role === "student" ? "e.g. 2nd Year" : "e.g. 2025-26"} className={inputCls} />
                </Field>
                <Field label="Department" required>
                  <input required data-testid="reg-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Computer Science" className={inputCls} />
                </Field>
                <Field label={role === "student" ? "Register Number" : "Staff/Register Number"} required>
                  <input required data-testid="reg-register_number" value={form.register_number} onChange={(e) => setForm({ ...form, register_number: e.target.value })} className={inputCls} />
                </Field>
                <Field label="College Name" required>
                  <input required data-testid="reg-college_name" value={form.college_name} onChange={(e) => setForm({ ...form, college_name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Mobile Number" required>
                  <input required data-testid="reg-mobile" type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91-XXXXXXXXXX" className={inputCls} />
                </Field>
                <Field label="Email Address" required>
                  <input required data-testid="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </Field>
                <div className="hidden md:block" />
                <Field label="Password" required>
                  <input required data-testid="reg-password" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Confirm Password" required>
                  <input required data-testid="reg-confirm_password" type="password" minLength={6} value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} className={inputCls} />
                </Field>
              </div>

              <label className="flex items-start gap-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                <input required type="checkbox" data-testid="reg-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5" />
                <span className="text-xs text-slate-600 leading-relaxed">
                  I agree that this website is intended <b>only for educational purposes</b>. I will not use it for any illegal, unauthorized, or non-educational activities. Any misuse may result in action by the website administrator and applicable government authorities.
                </span>
              </label>

              <button disabled={loading} data-testid="register-submit-btn"
                className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Creating account…" : role === "staff" ? "Request access" : "Create account"}
              </button>
              {role === "staff" && (
                <p className="text-xs text-slate-500 text-center">Staff accounts are reviewed by the administrator before access is granted.</p>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
